import { MEMBERS_AREA } from '@wix/app-definition-ids';
import { type IntegrationApplication } from '@wix/members-area-app-definitions';
import type { EditorSDK } from '@wix/platform-editor-sdk';
import { TPAComponentType } from '@wix/platform-editor-sdk';

import type { BiData } from '../../../../types/bi';
import { shouldAddStandalonePagesRoutesToPublicAppData } from '../../../../utils/experiments';
import { toMonitored } from '../../../../utils/monitoring';
import { withTimeoutMonitor } from '../../../../utils/promise-timeout';
import { isApplicationInstalled } from '../../../wrappers/application';
import { getAllPages, updatePageData } from '../../../wrappers/pages';
import { addApplication, addComponent, isAppSectionInstalled } from '../../../wrappers/tpa';
import { setStandaloneMemberPagesRoutes } from '../services/routes';

type AddPagesResponse = Awaited<ReturnType<typeof addPages>>;
type PageDefinition = ReturnType<typeof getPageDefinitions>[0];

export const getInstalledStandalonePageIds = async (editorSDK: EditorSDK) => {
  const allPagesData = await getAllPages({ editorSDK });
  const membersAreaPagesData = allPagesData.reduce<string[]>((tpaPageIds, pageData) => {
    const { managingAppDefId, tpaPageId } = pageData;
    return managingAppDefId === MEMBERS_AREA && tpaPageId ? [...tpaPageIds, tpaPageId] : tpaPageIds;
  }, []);

  return membersAreaPagesData;
};

const getNotInstalledStandalonePages = async (editorSDK: EditorSDK, definitions: IntegrationApplication[]) => {
  const notInstalledApplications = await Promise.all(
    definitions.map(async (definition) => {
      const { appDefinitionId, pageId } = definition;
      if (!pageId) {
        return { isInstalled: false, definition };
      }

      return {
        isInstalled: await isAppSectionInstalled({ editorSDK, appDefinitionId, sectionId: pageId }),
        definition,
      };
    }),
  );

  return notInstalledApplications.reduce<IntegrationApplication[]>((_definitions, { isInstalled, definition }) => {
    return isInstalled ? _definitions : [..._definitions, definition];
  }, []);
};

const getPageDefinitions = (appDefinitions: IntegrationApplication[], biData?: BiData) => {
  return appDefinitions.map((definition) => ({
    method: definition.method,
    appDefinitionId: definition.appDefinitionId,
    managingAppDefId: MEMBERS_AREA,
    componentType: TPAComponentType.Page,
    shouldNavigate: !!definition.shouldNavigate,
    page: {
      pageId: definition.pageId,
      shouldNavigate: !!definition.shouldNavigate,
      showInLoginMenu: !!definition.showInLoginMenu,
    },
    biData,
  }));
};

const ADD_APP_TIMEOUT_AMOUNT = 15000;

const addApplicationOrComponent = async (editorSDK: EditorSDK, _definition: PageDefinition) => {
  const definition = { ..._definition };
  const appDefinitionId = definition.appDefinitionId;
  const tpaPageId = definition.page.pageId;
  const addingMethod = definition.method;
  const isApplication = addingMethod === 'addApplication';
  delete definition.method;

  const timeoutMonitorName = isApplication ? 'v3.sdk.tpa.add.application' : 'v3.sdk.tpa.add.component';

  const action = async () => {
    // TODO: remove when uninstall is handled gracefully with all MA apps (currently DM's 'addApps' hang if you try to reinstall active app`)
    const isApplicationActive = await isApplicationInstalled(editorSDK, definition.appDefinitionId);

    return isApplication && !isApplicationActive
      ? { ...(await addApplication(editorSDK, definition)), appDefinitionId, tpaPageId }
      : { ...(await addComponent(editorSDK, definition)), appDefinitionId, tpaPageId };
  };

  const monitoringPayload = {
    extra: { definition },
    tags: { pageId: definition.page.pageId },
  };

  return withTimeoutMonitor(timeoutMonitorName, ADD_APP_TIMEOUT_AMOUNT, action, monitoringPayload);
};

const addPages = async (editorSDK: EditorSDK, pageDefinitions: PageDefinition[]) => {
  const addApplicationsOrComponentsPromises = pageDefinitions.map((definition) => {
    return addApplicationOrComponent(editorSDK, definition);
  });
  const addedApplicationsOrComponents = await Promise.all(addApplicationsOrComponentsPromises);
  // TODO: Handle verification same as in V1?
  return addedApplicationsOrComponents;
};

const setPagesData = (editorSDK: EditorSDK, pagesData: AddPagesResponse) => {
  const pageUpdates = pagesData.map((pageData) => {
    return 'pageRef' in pageData
      ? updatePageData({
          editorSDK,
          pageRef: pageData.pageRef,
          pageData: { indexable: false, pageSecurity: { requireLogin: true } },
        })
      : Promise.resolve();
  });

  return Promise.all(pageUpdates);
};

export const createStandalonePrivatePages = async (
  editorSDK: EditorSDK,
  definitions: IntegrationApplication[],
  biData?: BiData,
) => {
  const applicationDefinitions = await getNotInstalledStandalonePages(editorSDK, definitions);

  if (applicationDefinitions.length === 0) {
    return [];
  }

  const pages = await addPages(editorSDK, getPageDefinitions(applicationDefinitions, biData));

  await toMonitored('v3.createStandalonePrivatePages.setPagesData', () => setPagesData(editorSDK, pages));

  const addStandalonePagesRoutes = await shouldAddStandalonePagesRoutesToPublicAppData();

  if (addStandalonePagesRoutes) {
    const standaloneMemberPagesRoutes = definitions.map(({ widgetId, visibleForRoles, urlOverride }) => ({
      widgetId,
      vfr: visibleForRoles,
      path: urlOverride!,
    }));

    await setStandaloneMemberPagesRoutes(editorSDK, standaloneMemberPagesRoutes);
  }

  return pages;
};
